import React, { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { getCountryCallingCode } from 'react-phone-number-input';
import { useTranslation } from 'react-i18next';
import { getPreferredCountries } from '../../utils/phone';

/**
 * CountrySelect — seletor de país com BUSCA para o react-phone-number-input
 * (via prop `countrySelectComponent`). Substitui o <select> nativo (lista de ~240
 * países sem busca, inviável no mobile). Popover em portal (não é clipado por
 * modais/overflow), com busca por nome (accent-insensitive), ISO e DDI, e países
 * preferidos da região fixados no topo.
 *
 * Props injetadas pela lib: { value, options, onChange, iconComponent, disabled }.
 *   value      -> ISO do país atual (ou undefined = International)
 *   options    -> [{ value:'BR', label:'Brasil' }, ..., { value:undefined, label:'International' }]
 *   onChange   -> (iso|undefined) => void
 *   iconComponent(Icon) -> bandeira: <Icon country="BR" label="Brasil" />
 */

const normalize = (s) =>
  (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const dialCodeOf = (iso) => {
  if (!iso) return '';
  try { return getCountryCallingCode(iso); } catch { return ''; }
};

const CountrySelect = ({ value, options = [], onChange, iconComponent: Icon, disabled }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const inputRef = useRef(null);

  const preferred = useMemo(() => getPreferredCountries(), []);

  // Posiciona o popover (portal) ancorado no container .PhoneInput
  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const anchor = el.closest('.PhoneInput') || el;
    const r = anchor.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(Math.max(r.width, 300), vw - 16);
    let left = r.left;
    if (left + width > vw - 8) left = Math.max(8, vw - 8 - width);
    setCoords({ top: Math.round(r.bottom + 4), left: Math.round(left), width: Math.round(width) });
  }, []);

  useLayoutEffect(() => { if (open) reposition(); }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e) => {
      if (popRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onMove = () => reposition();
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, reposition]);

  // Foco na busca ao abrir
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  const list = useMemo(() => {
    const intl = options.find((o) => !o.value);
    const countries = options.filter((o) => o.value);
    const q = normalize(query);

    if (q) {
      const digits = q.replace(/\D/g, '');
      const all = intl ? [...countries, intl] : countries;
      return all.filter((o) => {
        if (!o.value) return normalize(o.label).includes(q);
        const name = normalize(o.label);
        const iso = String(o.value).toLowerCase();
        const dc = dialCodeOf(o.value);
        return name.includes(q) || iso === q || (digits && dc.startsWith(digits));
      });
    }

    // Sem busca: preferidos no topo (na ordem definida) → resto alfabético → International
    const prefSet = new Set(preferred);
    const top = preferred.map((iso) => countries.find((o) => o.value === iso)).filter(Boolean);
    const rest = countries.filter((o) => !prefSet.has(o.value));
    return intl ? [...top, ...rest, intl] : [...top, ...rest];
  }, [options, query, preferred]);

  const select = useCallback((iso) => {
    onChange(iso);
    setOpen(false);
    setQuery('');
  }, [onChange]);

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('phoneInput.searchPlaceholder', 'Buscar país')}
      >
        <FlagBox><Icon country={value} label={value || 'International'} /></FlagBox>
        <Arrow $open={open} />
      </Trigger>

      {open && coords && createPortal(
        <Popover ref={popRef} style={{ top: coords.top, left: coords.left, width: coords.width }} role="listbox">
          <SearchWrap>
            <SearchInput
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('phoneInput.searchPlaceholder', 'Buscar país…')}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </SearchWrap>
          <List>
            {list.length === 0 ? (
              <Empty>{t('phoneInput.noResults', 'Nenhum país encontrado')}</Empty>
            ) : (
              list.map((o) => {
                const dc = dialCodeOf(o.value);
                return (
                  <Item
                    key={o.value || 'intl'}
                    type="button"
                    $sel={o.value === value}
                    onClick={() => select(o.value)}
                  >
                    <ItemFlag><Icon country={o.value} label={o.label} /></ItemFlag>
                    <ItemName>{o.label}</ItemName>
                    {dc ? <ItemCode>+{dc}</ItemCode> : null}
                  </Item>
                );
              })
            )}
          </List>
        </Popover>,
        document.body
      )}
    </>
  );
};

// ===== styled =====

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  min-height: 42px;
  padding: 0 8px 0 12px;
  background: #f8f9fa;
  border: none;
  border-right: 1px solid #d9d9d9;
  cursor: pointer;
  flex-shrink: 0;
  &:disabled { cursor: not-allowed; opacity: 0.6; }
`;

const FlagBox = styled.span`
  display: flex;
  align-items: center;
  .PhoneInputCountryIcon { width: 24px; height: 18px; border-radius: 2px; overflow: hidden; box-shadow: 0 0 0 1px rgba(0,0,0,.1); }
  .PhoneInputCountryIconImg { width: 100%; height: 100%; object-fit: cover; }
`;

const Arrow = styled.span`
  display: block;
  width: 6px; height: 6px;
  border: solid #666;
  border-width: 0 1.5px 1.5px 0;
  transform: rotate(${(p) => (p.$open ? '-135deg' : '45deg')});
  margin-left: 2px;
  transition: transform 0.15s ease;
  flex-shrink: 0;
`;

const Popover = styled.div`
  position: fixed;
  z-index: 3000;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
  max-height: 45vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchWrap = styled.div`
  padding: 8px;
  border-bottom: 1px solid #eef2f6;
  background: #fff;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 15px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #1095F3; box-shadow: 0 0 0 3px rgba(16, 149, 243, 0.12); }
`;

const List = styled.div`
  overflow-y: auto;
  padding: 4px;
  -webkit-overflow-scrolling: touch;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  min-height: 44px;
  border: none;
  background: ${(p) => (p.$sel ? '#eef6fe' : 'transparent')};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  color: #1e293b;
  &:hover { background: #f1f5f9; }
`;

const ItemFlag = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  .PhoneInputCountryIcon { width: 26px; height: 19px; border-radius: 2px; overflow: hidden; box-shadow: 0 0 0 1px rgba(0,0,0,.08); }
  .PhoneInputCountryIconImg { width: 100%; height: 100%; object-fit: cover; }
`;

const ItemName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemCode = styled.span`
  color: #64748b;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

const Empty = styled.div`
  padding: 24px 12px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`;

export default CountrySelect;
